import { useCallback } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { CreateEventFeature, MIGRATED_PAGE_TO_EVENT_FEATURE_FLAG, MigratedCustomPageSlug } from '@shared/constants/customPageMigration';

type WebsiteAllPagesAsCustomPagesPayload = Readonly<{
  migratedPages: ReadonlyArray<MigratedCustomPageSlug>;
}>;

const isWebsiteAllPagesAsCustomPagesPayload = (payload: unknown): payload is WebsiteAllPagesAsCustomPagesPayload => {
  if (!payload || typeof payload !== 'object') return false;

  const migratedPages = (payload as { readonly migratedPages?: unknown }).migratedPages;
  if (!Array.isArray(migratedPages)) return false;

  return migratedPages.every(page => ['story', 'faq', 'vip', 'welcome', 'schedule'].includes(page));
};

/**
 * Returns a function that computes the eventFeatures array based on
 * currently enabled feature flags. These features are passed to the
 * backend during event creation to configure the new event.
 */
export const useCreateEventFeatures = () => {
  const [isWebsiteDesignerV3Enabled] = useIsFeatureEnabled('websiteDesignerV3Enabled');
  const [isWebsiteAllPagesAsCustomPagesEnabled, , , websiteAllPagesAsCustomPagesPayload] = useIsFeatureEnabled('websiteAllPagesAsCustomPagesEnabled');

  const getEventFeatures = useCallback((): Array<CreateEventFeature> => {
    const features: Array<CreateEventFeature> = [];

    if (isWebsiteDesignerV3Enabled) {
      // Enables the V3 website designer
      features.push('useWebsiteDesignerV3');
    }

    // Only derive per-page event features when the top-level remote flag is enabled *and*
    // the payload matches the expected shape, since remote payloads are untyped at runtime.
    if (isWebsiteAllPagesAsCustomPagesEnabled && isWebsiteAllPagesAsCustomPagesPayload(websiteAllPagesAsCustomPagesPayload)) {
      const { migratedPages } = websiteAllPagesAsCustomPagesPayload;

      migratedPages.forEach(page => features.push(MIGRATED_PAGE_TO_EVENT_FEATURE_FLAG[page]));
    }

    return features;
  }, [isWebsiteDesignerV3Enabled, isWebsiteAllPagesAsCustomPagesEnabled, websiteAllPagesAsCustomPagesPayload]);

  return { getEventFeatures };
};
